import type { Money } from "../../domain/money/money";
import type { UseCartResult } from "./useCart";

/**
 * What a module rendered in `till.cart.footer` may see of, and do to, the
 * cart (specs/006-loyalty contracts/core-extensions.md §1–2).
 *
 * Deliberately narrow. A module reads the lines and the total, may give one
 * unit of a line away (a grant), and may attach its own context to the cart
 * (an annotation). It cannot add, reprice or remove lines, and it cannot touch
 * a core discount.
 */
export interface CartExtensionLine {
  lineId: string;
  menuItemId: string;
  variantId: string | null;
  itemName: string;
  variantName: string | null;
  quantity: number;
  /** The variant's unit price, before modifiers. */
  unitPrice: Money;
  lineTotal: Money;
  grantedByModule: string | null;
}

export interface CartExtension {
  lines: readonly CartExtensionLine[];
  total: Money;
  grantLine(moduleId: string, lineId: string, label: string): string;
  revokeGrant(moduleId: string, lineId: string): void;
  annotate(moduleId: string, value: unknown): void;
  annotation(moduleId: string): unknown;
}

export function cartExtension(cart: UseCartResult): CartExtension {
  return {
    lines: cart.priced.lines.map((line) => ({
      lineId: line.lineId,
      menuItemId: line.menuItemId,
      variantId: line.variantId,
      itemName: line.itemName,
      variantName: line.variantName,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      lineTotal: line.lineTotal,
      grantedByModule: cart.grants[line.lineId]?.moduleId ?? null,
    })),
    total: cart.priced.total,
    grantLine: cart.grantLine,
    revokeGrant: cart.revokeGrant,
    annotate: cart.annotate,
    annotation: (moduleId) => cart.annotations[moduleId] ?? null,
  };
}

/** What the grants on this cart took off, for its own row in the totals. */
export function grantedTotal(cart: UseCartResult): Money {
  return cart.discountParts
    .filter((part) => cart.grants[part.lineId])
    .reduce((total, part) => total + part.grant, 0) as Money;
}
